import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, BuildingIcon, UserIcon } from 'lucide-react';
export function CustomerForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('id');
  const isEditMode = !!customerId;
  const [customerType, setCustomerType] = useState<'company' | 'individual'>('company');
  const [formData, setFormData] = useState({
    // Company
    companyName: isEditMode ? 'Acme Corporation' : '',
    contactPerson: '',
    taxId: '',
    // Individual
    fullName: '',
    // Shared
    email: isEditMode ? 'billing@acme.com' : '',
    phone: isEditMode ? '+66 12 345 6789' : '',
    addressLine1: isEditMode ? '123 Business Street' : '',
    addressLine2: '',
    city: isEditMode ? 'Bangkok' : '',
    province: '',
    country: 'Thailand',
    postcode: '',
    internalNotes: '',
    status: 'active' as 'active' | 'inactive'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Saving customer:', formData);
    navigate('/customers');
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  return <div className="max-w-3xl mx-auto space-y-6 pb-12" data-id="element-7478">
      {/* Header */}
      <div className="flex items-center space-x-4" data-id="element-7479">
        <button onClick={() => navigate('/customers')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-7480">
          <ArrowLeftIcon size={16} className="mr-2" data-id="element-7481" />
          Back to Customers
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-7482">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-7483">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-7484">
            {isEditMode ? 'Edit Customer' : 'Add New Customer'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-10" data-id="element-7485">
          {/* SECTION 1: Customer Information */}
          <div className="space-y-6" data-id="element-7486">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-7487">
              Customer Information
            </h2>

            {/* Customer Type Toggle */}
            <div className="flex p-1 bg-gray-100 rounded-lg w-fit" data-id="element-7488">
              <button type="button" onClick={() => setCustomerType('company')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${customerType === 'company' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-7489">
                <BuildingIcon size={16} className="mr-2" data-id="element-7490" />
                Company
              </button>
              <button type="button" onClick={() => setCustomerType('individual')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${customerType === 'individual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-7491">
                <UserIcon size={16} className="mr-2" data-id="element-7492" />
                Individual
              </button>
            </div>

            {customerType === 'company' ? <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-7493">
                <div data-id="element-7494">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7495">
                    Company Name *
                  </label>
                  <input type="text" name="companyName" required value={formData.companyName} onChange={handleChange} placeholder="Enter company name" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-7496" />
                </div>
                <div data-id="element-7497">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7498">
                    Contact Person
                  </label>
                  <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} placeholder="Enter contact person" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-7499" />
                </div>
                <div className="md:col-span-2" data-id="element-7500">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7501">
                    Tax ID / Registration Number
                  </label>
                  <input type="text" name="taxId" value={formData.taxId} onChange={handleChange} placeholder="13-digit registration number" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-7502" />
                </div>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-7503">
                <div className="md:col-span-2" data-id="element-7504">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7505">
                    Full Name *
                  </label>
                  <input type="text" name="fullName" required value={formData.fullName} onChange={handleChange} placeholder="Enter full name" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-7506" />
                </div>
              </div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-7507">
              <div data-id="element-7508">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7509">
                  Email Address *
                </label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder="billing@company.com" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-7510" />
              </div>
              <div data-id="element-7511">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7512">
                  Phone Number
                </label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+66 12 345 6789" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-7513" />
              </div>
            </div>
          </div>

          {/* Divider: Billing information */}
          <div className="relative py-2" data-id="element-7514">
            <div className="absolute inset-0 flex items-center" data-id="element-7515">
              <div className="w-full border-t border-gray-200" data-id="element-7516"></div>
            </div>
            <div className="relative flex justify-center" data-id="element-7517">
              <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-7518">
                Billing information
              </span>
            </div>
          </div>

          {/* SECTION 2: Billing Information */}
          <div className="space-y-5" data-id="element-7519">
            <div data-id="element-7520">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7521">
                Address Line 1
              </label>
              <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} placeholder="123 Business Street" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-7522" />
            </div>
            <div data-id="element-7523">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7524">
                Address Line 2
              </label>
              <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} placeholder="Suite, building, floor, etc." className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-7525" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-7526">
              <div data-id="element-7527">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7528">
                  City
                </label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="Bangkok" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-7529" />
              </div>
              <div data-id="element-7530">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7531">
                  Province
                </label>
                <input type="text" name="province" value={formData.province} onChange={handleChange} placeholder="Province" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-7532" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-7533">
              <div data-id="element-7534">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7535">
                  Country
                </label>
                <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 flex items-center" data-id="element-7536">
                  Thailand
                </div>
              </div>
              <div data-id="element-7537">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7538">
                  Postcode
                </label>
                <input type="text" name="postcode" value={formData.postcode} onChange={handleChange} placeholder="Postcode" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-7539" />
              </div>
            </div>
          </div>

          {/* Divider: Additional information */}
          <div className="relative py-2" data-id="element-7540">
            <div className="absolute inset-0 flex items-center" data-id="element-7541">
              <div className="w-full border-t border-gray-200" data-id="element-7542"></div>
            </div>
            <div className="relative flex justify-center" data-id="element-7543">
              <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-7544">
                Additional information
              </span>
            </div>
          </div>

          {/* SECTION 3: Additional Information */}
          <div className="space-y-5" data-id="element-7545">
            <div data-id="element-7546">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7547">
                Internal Notes
              </label>
              <textarea name="internalNotes" value={formData.internalNotes} onChange={handleChange} placeholder="Add any internal notes about this customer" rows={3} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors resize-none" data-id="element-7548" />
            </div>
            <div data-id="element-7549">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-7550">
                Status
              </label>
              <select name="status" value={formData.status} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors bg-white" data-id="element-7551">
                <option value="active" data-id="element-7552">Active</option>
                <option value="inactive" data-id="element-7553">Inactive</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-7554">
            <button type="button" onClick={() => navigate('/customers')} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-7555">
              Cancel
            </button>
            <button type="submit" className="px-6 py-2.5 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-7556">
              {isEditMode ? 'Update customer' : 'Create customer'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}